import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Reflects the embedded app's reported in-app path in the FinSuite address bar via the URL
 * HASH fragment (e.g. `/app/xpt#/reports/123?tab=summary`), for readable, copy-paste-friendly
 * deep links.
 *
 * The hash is invisible to `BrowserRouter` route matching, so the `/app/:urlName` route and
 * every match site (useEmbeddedApp, EmbedAppPage, …) are unaffected and the iframe is never
 * remounted — only the hash changes.
 *
 * Write-only: it mirrors the CURRENT page. Restoring a user INTO a page from a freshly loaded
 * hash is handled by useEmbedDeepLinkRestore, which pauses this hook (via `paused`) until the
 * restore lands so the child's initial "/" report can't clobber the deep link.
 *
 * NOTE — reported paths may contain their own hash (e.g. `/reports/123#row-4`). Mirroring the
 * whole path into our fragment then yields a URL with two `#` (`/app/xpt#/reports/123#row-4`).
 * This is nonstandard but browsers tolerate it, and `new URL()` still resolves the external link
 * correctly. The hash-restore parser MUST recover the reported path by stripping only the
 * FIRST `#` (i.e. `location.hash.slice(1)`) — never split on all `#`, which would truncate the
 * app's own fragment. (Encoding the inner `#` before reporting is not an option: it would leave a
 * literal `%23` in the external link instead of a real fragment.)
 *
 * @param reportedPath - Latest sanitized relative path from the embedded app, or null.
 * @param paused - When true, suppress hash writes (a deep-link restore is in progress).
 */
export const useSyncEmbedPathToHash = (reportedPath: string | null, paused = false): void => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // A deep-link restore is in progress — do not overwrite the pending hash from child reports.
    if (paused) return;

    // Nothing reported yet (or reset on env/app change): leave the current hash untouched so a
    // load-time deep-link hash is preserved for the future restore flow.
    if (!reportedPath) return;

    const desiredHash = `#${reportedPath}`;
    // Guard: skip when the hash already matches so identical repeated reports cause no churn.
    if (location.hash === desiredHash) return;

    // Reflect the path in the hash only; keep the current pathname + search (e.g. the ?t=
    // reset key). `replace` so in-app navigation doesn't stack browser history entries.
    navigate({ pathname: location.pathname, search: location.search, hash: desiredHash }, { replace: true });
  }, [reportedPath, paused, location.pathname, location.search, location.hash, navigate]);
};
